import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Edit3,
  Loader2,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { CalidadControlDocumentalPanel } from "../../../components/control-modificaciones/CalidadControlDocumentalPanel";
import { Iso900114001ConfirmDialog } from "../../../components/shared/Iso900114001ConfirmDialog";
import {
  Iso900114001Checkbox,
  Iso900114001Input,
  Iso900114001Select,
  Iso900114001Textarea,
} from "../../../components/shared/Iso900114001Fields";
import { Iso900114001Filters } from "../../../components/shared/Iso900114001Filters";
import { Iso900114001IconButton } from "../../../components/shared/Iso900114001IconButton";
import { Iso900114001MasterDetailTable } from "../../../components/shared/Iso900114001MasterDetailTable";
import { Iso900114001Modal } from "../../../components/shared/Iso900114001Modal";
import { Iso900114001OverviewCard } from "../../../components/shared/Iso900114001OverviewCard";
import { Iso900114001ScrollableCell } from "../../../components/shared/Iso900114001ScrollableCell";
import { Iso900114001SectionHeader } from "../../../components/shared/Iso900114001SectionHeader";
import { Iso900114001Tabs } from "../../../components/shared/Iso900114001Tabs";
import { getCertificationRequestError } from "../../../components/shared/request";
import {
  controlOperacionalControlEndpoint,
  deleteAspect,
  deleteGuideline,
  getControlOperacional,
  saveAspect,
  saveGuideline,
  updateAnnual,
} from "../api/control-operacional";
import type {
  AspectPayload,
  ControlOperacionalAspecto,
  ControlOperacionalPauta,
  ControlOperacionalResponse,
  GuidelinePayload,
} from "../types/control-operacional";
import { useAuthStore } from "@shared/store/auth";
import { useCanManageCertificaciones } from "@modules/certificaciones/shared/permissions/useCertificationPermissions";
import { useIso900114001DebouncedValue } from "../../../components/shared/useIso900114001FilteredRequest";

type Props = { onBack: () => void };
type Tab = "control" | "documental";
type ControlFilters = { search: string; resultado: string };

export function CalidadControlOperacionalSection({ onBack }: Props) {
  const [tab, setTab] = useState<Tab>("control");
  const [data, setData] = useState<ControlOperacionalResponse | null>(null);
  const [filters, setFilters] = useState<ControlFilters>({ search: "", resultado: "" });
  const [selectedAspectId, setSelectedAspectId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [aspectForm, setAspectForm] = useState<{
    open: boolean;
    id: number | null;
    value: AspectPayload;
  }>({
    open: false,
    id: null,
    value: { nombre: "", comentario_buenas_practicas: null, orden: null },
  });
  const [guidelineForm, setGuidelineForm] = useState<{
    open: boolean;
    id: number | null;
    value: GuidelinePayload;
  }>({
    open: false,
    id: null,
    value: { codigo: "", pauta_inspeccionar: "", resultado: null, orden: null },
  });
  const [deleteTarget, setDeleteTarget] = useState<{
    kind: "aspect" | "guideline";
    id: number;
    label: string;
  } | null>(null);
  const [annual, setAnnual] = useState({
    observaciones_comentarios: "",
    inspeccionado: false,
    fecha_inspeccion: "",
  });
  const [inspectorNameOverride, setInspectorNameOverride] = useState<string | null>(null);
  const currentUserName = useAuthStore((state) => state.me?.user.name ?? "Usuario actual");
  const canManageCertificaciones = useCanManageCertificaciones();
  const debouncedSearch = useIso900114001DebouncedValue(filters.search.trim(), 300);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getControlOperacional();
      setData(response);
      const record = response.data.registro_anual;
      setAnnual({
        observaciones_comentarios: record?.observaciones_comentarios ?? "",
        inspeccionado: record?.inspeccionado ?? false,
        fecha_inspeccion: record?.fecha_inspeccion ?? "",
      });
      setInspectorNameOverride(null);
      setSelectedAspectId((current) => (
        record?.aspectos.some((item) => item.id === current)
          ? current
          : record?.aspectos[0]?.id ?? null
      ));
    } catch (requestError) {
      setError(getCertificationRequestError(requestError, "No se pudo cargar el control operacional."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const record = data?.data.registro_anual ?? null;
  const aspects = record?.aspectos ?? [];
  const controls = data?.data.control_modificaciones ?? [];

  const resultOptions = useMemo(() => Array.from(new Set(
    aspects.flatMap((aspect) => aspect.pautas.map((row) => row.resultado).filter(Boolean) as string[]),
  )).sort((a, b) => a.localeCompare(b, "es")), [aspects]);

  const visibleAspects = useMemo(() => {
    const query = debouncedSearch.toLocaleLowerCase("es");
    return aspects.reduce<ControlOperacionalAspecto[]>((result, aspect) => {
      const matchingGuidelines = aspect.pautas.filter((guideline) => {
        const matchesResult = filters.resultado === "" || guideline.resultado === filters.resultado;
        const matchesSearch = query === "" || [
          aspect.nombre,
          aspect.comentario_buenas_practicas,
          guideline.codigo,
          guideline.pauta_inspeccionar,
          guideline.resultado,
        ].some((value) => String(value ?? "").toLocaleLowerCase("es").includes(query));
        return matchesResult && matchesSearch;
      });

      const aspectMatches = query === "" || [aspect.nombre, aspect.comentario_buenas_practicas]
        .some((value) => String(value ?? "").toLocaleLowerCase("es").includes(query));

      if ((filters.resultado === "" && aspectMatches) || matchingGuidelines.length > 0) {
        result.push({
          ...aspect,
          pautas: filters.resultado === "" && aspectMatches ? aspect.pautas : matchingGuidelines,
        });
      }
      return result;
    }, []);
  }, [aspects, debouncedSearch, filters.resultado]);

  useEffect(() => {
    if (visibleAspects.length === 0) {
      setSelectedAspectId(null);
      return;
    }
    if (!visibleAspects.some((item) => item.id === selectedAspectId)) {
      setSelectedAspectId(visibleAspects[0].id);
    }
  }, [selectedAspectId, visibleAspects]);

  const selectedAspect = useMemo(
    () => visibleAspects.find((item) => item.id === selectedAspectId) ?? null,
    [selectedAspectId, visibleAspects],
  );

  const openAspect = (row?: ControlOperacionalAspecto) => {
    setFormError(null);
    setAspectForm({
      open: true,
      id: row?.id ?? null,
      value: row
        ? {
            nombre: row.nombre,
            comentario_buenas_practicas: row.comentario_buenas_practicas,
            orden: row.orden,
          }
        : {
            nombre: "",
            comentario_buenas_practicas: null,
            orden: aspects.length + 1,
          },
    });
  };

  const openGuideline = (row?: ControlOperacionalPauta) => {
    if (!selectedAspect) return;
    setFormError(null);
    setGuidelineForm({
      open: true,
      id: row?.id ?? null,
      value: row
        ? {
            codigo: row.codigo,
            pauta_inspeccionar: row.pauta_inspeccionar,
            resultado: row.resultado,
            orden: row.orden,
          }
        : {
            codigo: "",
            pauta_inspeccionar: "",
            resultado: null,
            orden: selectedAspect.pautas.length + 1,
          },
    });
  };

  const submitAspect = async () => {
    if (!record) return;
    setSubmitting(true);
    setFormError(null);
    try {
      await saveAspect(aspectForm.id, {
        ...aspectForm.value,
        ...(aspectForm.id ? {} : { control_operacional_anio_id: record.id }),
      });
      setAspectForm((current) => ({ ...current, open: false }));
      await load();
    } catch (requestError) {
      setFormError(getCertificationRequestError(requestError, "No se pudo guardar el aspecto."));
    } finally {
      setSubmitting(false);
    }
  };

  const submitGuideline = async () => {
    if (!selectedAspect) return;
    setSubmitting(true);
    setFormError(null);
    try {
      await saveGuideline(guidelineForm.id, {
        ...guidelineForm.value,
        ...(guidelineForm.id ? {} : { aspecto_id: selectedAspect.id }),
      });
      setGuidelineForm((current) => ({ ...current, open: false }));
      await load();
    } catch (requestError) {
      setFormError(getCertificationRequestError(requestError, "No se pudo guardar la pauta."));
    } finally {
      setSubmitting(false);
    }
  };

  const saveAnnual = async () => {
    if (!record) return;
    setSubmitting(true);
    setFormError(null);
    try {
      await updateAnnual(record.id, {
        observaciones_comentarios: annual.observaciones_comentarios || null,
        inspeccionado: annual.inspeccionado,
        fecha_inspeccion: annual.fecha_inspeccion || null,
      });
      await load();
    } catch (requestError) {
      setFormError(getCertificationRequestError(requestError, "No se pudieron guardar los datos de inspección."));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      if (deleteTarget.kind === "aspect") {
        await deleteAspect(deleteTarget.id);
      } else {
        await deleteGuideline(deleteTarget.id);
      }
      setDeleteTarget(null);
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="iso9001-section-stack">
      <Iso900114001SectionHeader
        eyebrow="Calidad y medioambiente"
        title="Control operacional"
        description="Registro lineal de aspectos, pautas de inspección, comentarios y datos globales de la inspección."
        onBack={onBack}
        onRefresh={() => void load()}
        refreshing={loading}
      />

      <div className="iso9001-overview-grid">
        <Iso900114001OverviewCard icon={ClipboardCheck} label="Aspectos" value={data?.meta.aspectos_total ?? 0} />
        <Iso900114001OverviewCard icon={CheckCircle2} label="Pautas" value={data?.meta.pautas_total ?? 0} />
        <Iso900114001OverviewCard icon={ClipboardList} label="Control documental" value={controls.length} />
      </div>

      <Iso900114001Tabs
        tabs={[
          { id: "control", label: "Control operacional", count: data?.meta.pautas_total ?? 0 },
          { id: "documental", label: "Control documental", count: controls.length },
        ]}
        activeTab={tab}
        onChange={setTab}
        ariaLabel="Control operacional"
      />

      {tab === "documental" ? (
        <CalidadControlDocumentalPanel
          rows={controls}
          loading={loading}
          error={error}
          baseEndpoint={controlOperacionalControlEndpoint}
          onReload={load}
        />
      ) : (
        <>
          <Iso900114001Filters
            title="Filtrar control operacional"
            count={visibleAspects.reduce((total, aspect) => total + aspect.pautas.length, 0)}
            onReset={() => setFilters({ search: "", resultado: "" })}
          >
            <Iso900114001Input
              label="Búsqueda"
              value={filters.search}
              onChange={(event) => setFilters({ ...filters, search: event.target.value })}
              placeholder="Aspecto, código, pauta o comentario"
            />
            <Iso900114001Select
              label="Resultado"
              value={filters.resultado}
              onChange={(event) => setFilters({ ...filters, resultado: event.target.value })}
            >
              <option value="">Todos</option>
              {resultOptions.map((value) => <option key={value} value={value}>{value}</option>)}
            </Iso900114001Select>
          </Iso900114001Filters>

          {error ? <div className="reqleg-inline-error">{error}</div> : null}

          <Iso900114001MasterDetailTable
            eyebrow="Control operacional"
            title="Aspectos y pautas de inspección"
            countLabel={`${visibleAspects.length} aspectos`}
            createLabel="Nuevo aspecto"
            items={visibleAspects.map((aspect) => ({
              id: aspect.id,
              label: aspect.nombre,
              shortLabel: aspect.nombre.slice(0, 2).toUpperCase(),
              title: aspect.nombre,
              count: aspect.pautas.length,
            }))}
            selectedId={selectedAspectId}
            loading={loading}
            emptyText="No hay aspectos para los filtros seleccionados."
            onCreate={() => openAspect()}
            onSelect={(id) => setSelectedAspectId(Number(id))}
          >
            {!selectedAspect ? (
              <div className="iso9001-master-detail-empty iso9001-master-detail-empty-large">
                Selecciona o crea un aspecto.
              </div>
            ) : (
              <>
                <div className="iso9001-detail-heading">
                  <div>
                    <div className="iso9001-section-eyebrow">Aspecto seleccionado</div>
                    <h3>{selectedAspect.nombre}</h3>
                    <Iso900114001ScrollableCell title={selectedAspect.comentario_buenas_practicas ?? undefined}>
                      {selectedAspect.comentario_buenas_practicas || "Sin comentario de buenas prácticas."}
                    </Iso900114001ScrollableCell>
                  </div>
                  <div className="calidad-table-actions-row">
                    <Iso900114001IconButton
                      label="Editar aspecto"
                      icon={<Edit3 size={15} />}
                      onClick={() => openAspect(selectedAspect)}
                    />
                    <Iso900114001IconButton
                      label="Eliminar aspecto"
                      icon={<Trash2 size={15} />}
                      danger
                      onClick={() => setDeleteTarget({
                        kind: "aspect",
                        id: selectedAspect.id,
                        label: selectedAspect.nombre,
                      })}
                    />
                  </div>
                </div>

                <div className="iso9001-subtable-heading">
                  <h4>Pautas a inspeccionar</h4>
                  <button type="button" className="calidad-primary-button" onClick={() => openGuideline()}>
                    <Plus size={15} />
                    Añadir pauta
                  </button>
                </div>

                <div className="iso9001-crud-scroll">
                  <table className="calidad-table iso9001-crud-table">
                    <thead>
                      <tr>
                        <th className="iso9001-actions-column">Acciones</th>
                        <th>Código</th>
                        <th className="iso9001-extra-long-column">Pauta a inspeccionar</th>
                        <th>R*</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAspect.pautas.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="iso9001-empty-table-cell">
                            No hay pautas para el aspecto seleccionado.
                          </td>
                        </tr>
                      ) : selectedAspect.pautas.map((row) => (
                        <tr key={row.id}>
                          <td className="iso9001-actions-column">
                            <div className="calidad-table-actions-row">
                              <Iso900114001IconButton
                                label="Editar pauta"
                                icon={<Edit3 size={14} />}
                                onClick={() => openGuideline(row)}
                              />
                              <Iso900114001IconButton
                                label="Eliminar pauta"
                                icon={<Trash2 size={14} />}
                                danger
                                onClick={() => setDeleteTarget({
                                  kind: "guideline",
                                  id: row.id,
                                  label: row.pauta_inspeccionar,
                                })}
                              />
                            </div>
                          </td>
                          <td>{row.codigo || "—"}</td>
                          <td className="iso9001-extra-long-column">
                            <Iso900114001ScrollableCell title={row.pauta_inspeccionar}>
                              {row.pauta_inspeccionar}
                            </Iso900114001ScrollableCell>
                          </td>
                          <td>{row.resultado || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Iso900114001MasterDetailTable>

          <section className="iso9001-global-block">
            <div className="iso9001-global-block-heading">
              <div>
                <div className="iso9001-section-eyebrow">Datos globales</div>
                <h3>Inspección y observaciones</h3>
              </div>
              <button
                type="button"
                className="calidad-primary-button"
                disabled={!canManageCertificaciones || !record || submitting}
                onClick={() => void saveAnnual()}
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Guardar
              </button>
            </div>
            {formError ? <div className="reqleg-inline-error">{formError}</div> : null}
            <div className="iso9001-form-grid">
              <Iso900114001Textarea
                label="Observaciones y comentarios de interés"
                fullWidth
                rows={5}
                disabled={!canManageCertificaciones}
                value={annual.observaciones_comentarios}
                onChange={(event) => setAnnual({ ...annual, observaciones_comentarios: event.target.value })}
              />
              <div className="iso9001-field">
                <span>Inspeccionado por (firma)</span>
                <Iso900114001Checkbox
                  label={annual.inspeccionado
                    ? inspectorNameOverride || record?.inspeccionado_por_nombre || currentUserName
                    : "Marcar como inspeccionado"}
                  checked={annual.inspeccionado}
                  disabled={!canManageCertificaciones}
                  onChange={(checked) => {
                    setAnnual({ ...annual, inspeccionado: checked });
                    setInspectorNameOverride(checked ? currentUserName : null);
                  }}
                />
              </div>
              <Iso900114001Input
                label="Fecha de inspección"
                type="date"
                disabled={!canManageCertificaciones}
                value={annual.fecha_inspeccion}
                onChange={(event) => setAnnual({ ...annual, fecha_inspeccion: event.target.value })}
              />
            </div>
          </section>
        </>
      )}

      <Iso900114001Modal
        open={aspectForm.open}
        title={aspectForm.id ? "Editar aspecto" : "Nuevo aspecto"}
        submitting={submitting}
        error={formError}
        onClose={() => setAspectForm((current) => ({ ...current, open: false }))}
        onSubmit={() => void submitAspect()}
        footer={(
          <>
            <button type="button" className="calidad-back-button" onClick={() => setAspectForm((current) => ({ ...current, open: false }))}>
              Cancelar
            </button>
            <button type="submit" className="calidad-primary-button" disabled={submitting}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Guardar
            </button>
          </>
        )}
      >
        <Iso900114001Input label="Aspecto" required fullWidth value={aspectForm.value.nombre} onChange={(event) => setAspectForm({ ...aspectForm, value: { ...aspectForm.value, nombre: event.target.value } })} />
        <Iso900114001Textarea label="Comentario / buenas prácticas" fullWidth rows={5} value={aspectForm.value.comentario_buenas_practicas ?? ""} onChange={(event) => setAspectForm({ ...aspectForm, value: { ...aspectForm.value, comentario_buenas_practicas: event.target.value || null } })} />
        <Iso900114001Input label="Orden" type="number" value={aspectForm.value.orden ?? ""} onChange={(event) => setAspectForm({ ...aspectForm, value: { ...aspectForm.value, orden: event.target.value === "" ? null : Number(event.target.value) } })} />
      </Iso900114001Modal>

      <Iso900114001Modal
        open={guidelineForm.open}
        title={guidelineForm.id ? "Editar pauta" : "Nueva pauta"}
        submitting={submitting}
        error={formError}
        onClose={() => setGuidelineForm((current) => ({ ...current, open: false }))}
        onSubmit={() => void submitGuideline()}
        footer={(
          <>
            <button type="button" className="calidad-back-button" onClick={() => setGuidelineForm((current) => ({ ...current, open: false }))}>
              Cancelar
            </button>
            <button type="submit" className="calidad-primary-button" disabled={submitting}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Guardar
            </button>
          </>
        )}
      >
        <Iso900114001Input label="Código" value={guidelineForm.value.codigo ?? ""} onChange={(event) => setGuidelineForm({ ...guidelineForm, value: { ...guidelineForm.value, codigo: event.target.value || null } })} />
        <Iso900114001Input label="Resultado (R*)" value={guidelineForm.value.resultado ?? ""} onChange={(event) => setGuidelineForm({ ...guidelineForm, value: { ...guidelineForm.value, resultado: event.target.value || null } })} placeholder="C, Df, NCp, NA, X, 0, 1..." />
        <Iso900114001Textarea label="Pauta a inspeccionar" fullWidth required rows={5} value={guidelineForm.value.pauta_inspeccionar} onChange={(event) => setGuidelineForm({ ...guidelineForm, value: { ...guidelineForm.value, pauta_inspeccionar: event.target.value } })} />
        <Iso900114001Input label="Orden" type="number" value={guidelineForm.value.orden ?? ""} onChange={(event) => setGuidelineForm({ ...guidelineForm, value: { ...guidelineForm.value, orden: event.target.value === "" ? null : Number(event.target.value) } })} />
      </Iso900114001Modal>

      <Iso900114001ConfirmDialog
        open={deleteTarget !== null}
        title="Eliminar registro"
        description={`Se eliminará ${deleteTarget?.label ?? "el registro"}.`}
        confirming={submitting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void remove()}
      />
    </section>
  );
}
